import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';

import { AppIcons } from '../../../../core/icons/fa-icons';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { PKL_GRP } from '../../../../core/shared/models/picklist.model';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { TableColumn, TableAction } from '../../../shared/models/table-models';
import { Setup } from '../../shared/service/setup';
import { Table } from '../../../shared/table/table';
import { Router } from '@angular/router';
import { BackButton } from '../../../shared/buttons/back-button';

@Component({
  selector: 'app-district',
  standalone: true,
  imports: [TranslatePipe, CommonModule, Table, Popup, BackButton],
  templateUrl: './district.html',
  styleUrl: './district.css',
})
export class District implements OnInit {
  icons = AppIcons;

  stateSeq!: number;

  list: any[] = [];
  columns: TableColumn[] = [];

  showPopup = false;
  isEditMode = false;
  isViewMode = false;
  formData: any = {};

  fields: DynamicFieldConfig[] = [];

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private setup: Setup,
    private toastr: ToastrService,
    private picklist: PickListService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.stateSeq = Number(params.get('id'));
      this.loadPicklists();
    });
  }

  // ================= ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.view,
      tooltip: 'COMMON.VIEW',
      class: 'action-view',
      onClick: (row) => this.openPopup(row, true),
    },
    {
      icon: this.icons.delete,
      tooltip: 'COMMON.DELETE',
      class: 'action-delete',
      onClick: (row) => this.openDeletePopup(row),
    },
  ];

  // ================= DELETE CONFIRM =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.name });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    this.delete(this.selectedRow);
    this.selectedRow = null;
  }

  // ================= INIT =================
  loadPicklists() {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.buildFields();
      this.buildColumns();
      this.loadData();
    });
  }
goToTehsil(row: any) {
  this.router.navigate(['/tehsil', row.dstrctSeq]);
}
  // ================= FORM =================
  buildFields() {
    this.fields = [
      {
        key: 'dstrctSeq',
        label: 'SETUP.ID',
        type: 'text',
        hidden: true,
      },
      {
        key: 'name',
        label: 'COMMON.NAME',
        type: 'text',
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: { required: true },
      },
      {
        key: 'description',
        label: 'COMMON.DESCRIPTION',
        type: 'textarea',
      },
    ];
  }

  // ================= TABLE =================
  buildColumns() {
    this.columns = [
      { key: 'dstrctSeq', label: 'SETUP.ID' },

      {
        key: 'name',
        label: 'COMMON.NAME',
        type: 'link',
        onClick: (row: any) => this.goToTehsil(row),
      },

      { key: 'description', label: 'COMMON.DESCRIPTION' },

      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: {
          706: 'COMMON.ACTIVE',
          707: 'COMMON.INACTIVE',
        },
        badgeMap: {
          706: 'status-active',
          707: 'status-inactive',
        },
      },
    ];
  }

  // ================= LOAD =================
  loadData() {
    this.setup.getDistricts(this.stateSeq).subscribe({
      next: (res: any) => {
        console.log('RAW DISTRICTS RESPONSE:', res);

        // ✅ handle error response
        if (!res || res.error) {
          this.list = [];
          this.toastr.warning(res?.error || this.translate.instant('COMMON.NO_DATA_FOUND'));
          return;
        }

        // ✅ normalize response to array
        const data = Array.isArray(res) ? res : [];

        this.list = data.map((d: any) => ({
          dstrctSeq: d.dstrctSeq,
          name: d.dstrctNm,
          description: d.dstrctDesc,
          status: d.dstrctStsKey,
        }));
      },

      error: () => {
        this.list = [];
        this.toastr.error(this.translate.instant('SETUP.GEOGRAPHY.LOAD_DISTRICTS_FAILED'));
      },
    });
  }

  // ================= POPUP =================
  openPopup(data?: any, viewMode: boolean = false) {
    this.showPopup = true;
    this.isViewMode = viewMode;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        dstrctSeq: data.dstrctSeq,
        name: data.name,
        description: data.description,
        status: data.status,
      };
    } else {
      this.isEditMode = false;

      this.formData = {
        dstrctSeq: null,
        status: this.getDefaultStatus(),
      };
    }
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || null;
  }

  // ================= SAVE =================
  handleSave = (form: any) => {
    const payload = {
      dstrctSeq: this.isEditMode ? form.dstrctSeq : null,
      stSeq: this.stateSeq,
      dstrctCd: '',
      dstrctDesc: form.description,
      dstrctNm: form.name,
      dstrctStsKey: form.status, // DIRECT dropdown value
      delFlg: 0,
      tntKey: 0,
    };

    return this.setup.saveDistrict(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant(this.isEditMode ? 'SETUP.GEOGRAPHY.DISTRICT_UPDATED' : 'SETUP.GEOGRAPHY.DISTRICT_CREATED'));
        this.showPopup = false;
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED')),
    });
  };

  // ================= DELETE =================
  delete(row: any) {
    const payload = {
      dstrctSeq: row.dstrctSeq,
      stSeq: this.stateSeq,
      delFlg: 1,
    };

    this.setup.saveDistrict(payload).subscribe({
      next: (res: any) => {
        if (res?.error) {
          this.toastr.error(res.error);
          return;
        }

        this.toastr.success(this.translate.instant('SETUP.DELETED_SUCCESSFULLY'));

        // 🔥 force clean reload
        this.list = []; // clear UI first
        this.loadData(); // reload fresh data
      },

      error: () => {
        this.toastr.error(this.translate.instant('SETUP.DELETE_FAILED'));
      },
    });
  }
}
